import React, {ReactNode, useCallback, memo, useRef} from 'react';
import cx from 'classnames';
import styles from './style.module.scss';
import {createDataTestId} from '../../../../utils/dataTestId';

type Props = {
    children?: ReactNode;
    resizable?: boolean;
    onResizeEnd?: (height: number) => void;
    minHeight?: number;
    defaultHeight?: number;
    isActive?: boolean;
};

const DEFAULT_HEIGHT = 400;
const DEFAULT_MIN_HEIGHT = 210;

export const Splitter = memo(
    ({
        children,
        resizable,
        onResizeEnd,
        defaultHeight = DEFAULT_HEIGHT,
        minHeight = DEFAULT_MIN_HEIGHT
    }: Props) => {
        const startYPosition = useRef(0);
        const height = useRef(defaultHeight);
        const wrapRef = useRef<HTMLDivElement>();
        const handleWrapRef = useRef<HTMLDivElement>();

        const getNewHeight = useCallback(
            (offset: number) => {
                return Math.max(height.current + offset - startYPosition.current, minHeight);
            },
            [minHeight]
        );

        const onMouseMove = useCallback(
            e => {
                const el = wrapRef.current;
                if (el) el.style.height = `${getNewHeight(e.clientY)}px`;
            },
            [getNewHeight]
        );

        const onMouseUp = useCallback(
            e => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);

                height.current = getNewHeight(e.clientY);

                handleWrapRef.current.classList.remove(styles.active);
                onResizeEnd?.(height.current);
            },
            [onMouseMove, getNewHeight, onResizeEnd]
        );

        const onMouseDown = useCallback(
            e => {
                startYPosition.current = e.clientY;
                handleWrapRef.current.classList.add(styles.active);
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
            },
            [onMouseUp, onMouseMove]
        );

        if (resizable) {
            return (
                <>
                    <div
                        className={styles.component}
                        ref={wrapRef}
                        style={{height: height.current}}
                    >
                        {children}
                    </div>
                    <div className={styles.handleWrap} ref={handleWrapRef}>
                        <div
                            {...createDataTestId('splitter-handle', false)}
                            className={cx(styles.handle)}
                            onMouseDown={onMouseDown}
                            onMouseUp={onMouseUp}
                        >
                            <div className={styles.defaultIcon} />
                        </div>
                    </div>
                </>
            );
        }
        return <>{children}</>;
    }
);
